import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import useNotification from "../../components/notifications/useNotifications";
import useVehicleLookupMode from "../../hooks/useVehicleLookupMode";
import {
  identifierField,
  identifierValidationMessage,
  isValidIdentifier,
  normaliseIdentifier,
  vehicleDisplayIdentifier,
} from "../../utils/vehicleIdentifier";
import GetAddresses from "../../components/shared/Addresses/GetAddresses";
import Breadcrumbs from "../../components/shared/Breadcrumbs/Breadcrumbs";
import Card from "../../components/shared/Card/Card";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { NumberInput } from "../../components/shared/Inputs/NumberInput";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useLeads from "../../data/useLeads";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import usePricingAccess from "../../hooks/usePricingAccess";
import { INewLead, LeadSource } from "../../models/Lead";
import { RadioGroupInput } from "../../components/shared/Inputs/RadioGroupInput";
import { ContactCombobox } from "../../components/contacts/ContactCombobox";
import { IContact } from "../../models/Contact";
import useAccount from "../../data/useAccount";
import { IAccount } from "../../models/account/IAccount";



// *********************************** //

// *** THIS IS NOT IN USE ANYMORE *** //
// ***   PLEASE DON'T IGNORE ME   *** //
// ***    IT HURTS MY FEELINGS    *** //

// *********************************** //


/**
 * @deprecated Nothing links here any more - "Create Lead" goes to CreateLeadMultipleVehicles
 * (leads/create-multiple). Kept reachable at leads/create and bookings/create; it has none of the
 * duplicate-contact checks, so build new work on CreateLeadMultipleVehicles.
 */
export default function CreateLead() {
  const { t } = useTranslation("leads");
  const { canEditPrices } = usePricingAccess();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isBookingPath = pathname.startsWith("/bookings");
  const leadLabel = isBookingPath ? "Booking" : "Lead";
  const leadListLabel = isBookingPath ? "Bookings" : "Leads";
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  const { setInfo } = usePageNameContext();
  const { create } = useLeads(true, "CreateLead");
  const { vrmLookup } = useVehiclesMutations();
  const { identifierType, isVinMode } = useVehicleLookupMode();
  const [address, setAddress] = useState<any>();
  const [postcode, setPostcode] = useState("");
  const [currentSource, setCurrentSource] = useState("GPM");
  const [addressesOpen, setAddressesOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { register, getValues, setValue, watch, handleSubmit } = useForm<any>({
    defaultValues: {
      isDropOff: false,
      commission: 0,
      price: 0
    },
  });
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null);
  const { account } = useAccount(selectedContact?.accountId, "CreateLead");
  const [selectedAccount, setSelectedAccount] = useState<IAccount | null>(null);

  const sources = [
    ...Object.values(LeadSource).filter((ct) => isNaN(Number(ct))),
  ];

  const onSubmit = (data: any) => {
    setIsLoading(true);

    var newLead: INewLead = {
      CCID: null,
      website: parseInt(currentSource),
      source: "Manual",
      price: data.price,
      commission: data.commission ?? 0,
      vehicleGrade: data.vehicleGrade,
      vehicle: {
        vrm: data.vrm,
        vin: data.vin,
        make: data.make,
        model: data.model,
        postcode: data.postcode,
        line1: data.line1,
        line2: data.line2,
        line3: data.line3,
        line4: data.line4,
        locality: data.locality,
        town: data.town,
        county: data.county,
        isDropOff: data.isDropOff === 1 ? true : false,
      },
      contact: {
        contactId: data.contactId || "00000000-0000-0000-0000-000000000000",
        fullname: data.fullname,
        email: data.email === "" ? null : data.email,
        telephone1: data.telephone1,
        telephone2: data.telephone2,
      },
    };

    create.mutate(newLead, {
      onSuccess: (data) => {
        saveHandlers.onSuccess();
        setIsLoading(false);
        navigate("..");
      },
      onError: (error) => {
        saveHandlers.onError();
        setIsLoading(false);
      },
    });
  };

  const handlePostcodeChange = () => {
    if (getValues("postcode") !== postcode) {
      setPostcode(getValues("postcode"));
    } 
    setAddressesOpen(true);
  };

  const handleVrmLookup = () => {
    const identifier = normaliseIdentifier(getValues(identifierField(identifierType)), identifierType);

    if (!isValidIdentifier(identifier, identifierType)) {
      addNotification({
        variant: "error",
        primaryText: t(identifierValidationMessage(identifierType)),
      });
      return;
    }

    vrmLookup.mutate({ identifier, registration: isVinMode ? getValues("vrm") : undefined }, {
      onSuccess: (data) => {
        const vehicle = data.data;
        if (!vehicle) {
          addNotification({
            variant: "error",
            primaryText: t(isVinMode ? "Error looking up VIN" : "Error looking up VRM"),
          });
          return;
        }

        if (isVinMode && vehicle.vrm) setValue("vrm", vehicle.vrm);
        setValue("make", vehicle.combinedMake);
        setValue("model", vehicle.combinedModel);
        addNotification({
          variant: "success",
          primaryText: t("Found vehicle"),
        });

        if (data.existed) {
          addNotification({
            variant: "info",
            primaryText: t("Vehicle has been added previously"),
          });
        }
      },
      onError: (error: any) => {
        console.error(error);
        addNotification({
          variant: "error",
          primaryText: t(isVinMode ? "Error looking up VIN" : "Error looking up VRM"),
          secondaryText: error?.message,
        });
      },
    });
  };

  useEffect(() => {
    setInfo({
      name: isBookingPath ? t("New Booking") : t("New Lead"),
      desc: isBookingPath ? t("Create a new manual booking") : t("Create a new manual lead"),
    });
  }, [leadLabel]);

  useEffect(() => {
    if (address) {
      setValue("line1", address.line1);
      setValue("line2", address.line2);
      setValue("line3", address.line3);
      setValue("line4", address.line4);
      setValue("locality", address.locality);
      setValue("town", address.town);
      setValue("county", address.county);
      setValue("postcode", address.postcode);
    }
  }, [address]);

  useEffect(() => {
    setValue("fullname", selectedContact?.fullname || "");
    setValue("email", selectedContact?.email || "");
    setValue("telephone1", selectedContact?.telephone1 || "");
    setValue("telephone2", selectedContact?.telephone2 || "");
    setValue("contactId", selectedContact?.id || "");

    if (selectedContact && selectedContact.accountId) account.refetch();
  }, [selectedContact]);

  useEffect(() => {
    setSelectedAccount(account?.data?.data || null);
  }, [account]);

  const pages = [
    { to: "..", label: t(leadListLabel), current: false },
    {
      to: "",
      label: isBookingPath ? t("New Booking") : t("New Lead"),
      current: true,
    },
  ];

  if (isLoading) {
    return (
      <div className="pt-[20vh] flex justify-center items-center h-full">
        <LoadingWheel />
      </div>
    );
  } else {
    return (
      <>
        <GetAddresses
          postcode={postcode}
          setAddress={setAddress}
          open={addressesOpen}
          setOpen={setAddressesOpen}
        />
        <Breadcrumbs pages={pages} />

        <Card containerClassName="mt-4">
          <form onSubmit={handleSubmit(onSubmit)} className="px-6 pt-4 pb-8">
            <h1 className="mb-2 text-lg font-semibold">{isBookingPath ? t("Booking Details") : t("Lead Details")}</h1>
            <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:gap-y-4 sm:grid-cols-2">
              {/* Website select */}
              {canEditPrices && (
                <NumberInput
                  label={t("Price")}
                  register={register}
                  registerName="price"
                />
              )}
              {canEditPrices && (
                <NumberInput
                  label={t("Commission")}
                  register={register}
                  registerName="commission"
                />
              )}
              <select
                aria-label="Lead source"
                onChange={(e) => setCurrentSource(e.target.value)}
                className="sm:text-sm"
              >
                {sources.map((source, i) => (
                  <option className="sm:text-sm" key={source} value={i}>
                    {t(String(source))}
                  </option>
                ))}
              </select>
              <RadioGroupInput
                options={[
                  { name: t("Collection"), value: 0 },
                  { name: t("Drop Off"), value: 1 },
                ]}
                register={register}
                registerName={"isDropOff"}
                setValue={setValue}
                watch={watch}
              />
            </div>

            <h1 className="mt-6 mb-2 text-lg font-semibold">{t("Vehicle Details")}</h1>
            <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:gap-y-4 sm:grid-cols-2">
              {isVinMode ? (
                <TextInput
                  label={t("VIN")}
                  register={register}
                  registerName="vin"
                  required
                  maxLength={17}
                  placeholder="e.g. WVWZZZ1KZAW123456"
                />
              ) : (
                <TextInput label={t("VRM or VIN")} register={register} registerName="vrm" required />
              )}
              {isVinMode && (
                <TextInput label={t("Registration (optional)")} register={register} registerName="vrm" />
              )}
              <div className="flex items-center mt-7">
                <ButtonInput
                  label={t("Find Vehicle")}
                  isSubmit={false}
                  classes="w-40 h-10"
                  onClick={() => handleVrmLookup()}
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:gap-y-4 sm:grid-cols-2">
              <TextInput label={t("Make")} register={register} registerName="make" />
              <TextInput
                label={t("Model")}
                register={register}
                registerName="model"
              />
              <TextInput
                label={t("Vehicle Grade")}
                register={register}
                registerName="vehicleGrade"
              />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:gap-y-4 sm:grid-cols-2">
              <TextInput
                label={t("Postcode")}
                register={register}
                registerName="postcode"
              />
              <div className="flex items-center mt-7">
                <ButtonInput
                  label={t("Get Addresses")}
                  isSubmit={false}
                  classes="w-40 h-10"
                  onClick={() => handlePostcodeChange()}
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:gap-y-4 sm:grid-cols-2">
              <TextInput
                label={t("Line 1")}
                register={register}
                registerName="line1"
              />
              <TextInput
                label={t("Line 2")}
                register={register}
                registerName="line2"
              />
              <TextInput
                label={t("Line 3")}
                register={register}
                registerName="line3"
              />
              <TextInput
                label={t("Line 4")}
                register={register}
                registerName="line4"
              />
              <TextInput
                label={t("Locality")}
                register={register}
                registerName="locality"
              />
              <TextInput label={t("Town")} register={register} registerName="town" />
              <TextInput
                label={t("County")}
                register={register}
                registerName="county"
              />
            </div>

            <h1 className="mt-6 mb-4 text-lg font-semibold">{t("Contact Details")}</h1>
            <ContactCombobox label={t("Select Contact")} onContactSelect={setSelectedContact} /><div></div>
            <fieldset className="grid grid-cols-1 gap-x-8 gap-y-4 sm:gap-y-4 sm:grid-cols-2">
              
      
              <legend className="text-sm font-semibold py-4">{t("Or create contact")}</legend>


              <input type="hidden" value={selectedContact?.id || ""} {...register("contactId")} />
              <TextInput
                label={t("Full Name")}
                register={register}
                registerName="fullname"
              />
              <TextInput
                label={t("Email Address")}
                register={register}
                registerName="email"
              />
              <TextInput
                label={t("Main Telephone")}
                register={register}
                registerName="telephone1"
              />
              <TextInput
                label={t("Additional Telephone")}
                register={register}
                registerName="telephone2"
              />
            </fieldset>
            {/* {selectedContact && <p className="text-xs mt-4 text-amber-700"><i>*ID Documents are uploaded for this contact*</i></p>} */}
            {selectedAccount && (
              <div className="mt-4 w-fit">
                <h2 className="text-sm font-semibold mb-2">{t("Selected Account")}</h2>
                <div className="p-4 bg-gray-100 rounded-md">
                  <p className="text-sm font-medium">{selectedAccount.name}</p>
                  <p className="text-sm font-medium">{selectedAccount.bankSortCode} / {selectedAccount.bankAccountNumber}</p>
                </div>
              </div>
            )}
            

            <ButtonInput
              label={isBookingPath ? t("Create Booking") : t("Create Lead")}
              isSubmit={true}
              classes="mt-8"
              onClick={null}
            />
          </form>
        </Card>
      </>
    );
  }
}
